import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import { plus, minus, marker, sustainability } from 'components/atoms/Icon/icons';
import * as styles from './ProductPresentationTabs.scss';
import { forceCheck } from 'react-lazyload';
import { remove } from 'lodash';

// just mocking some stuff here until we see that it works as intended...

const Tab = ({ label, id, children }) => null;

const hiddenStyle = { display: 'none' };

const Tabs = ({ tabs, current, viewedTabs, getUrlForTab }) => (
  <React.Fragment>
    <ul className={styles.tabs}>
      {tabs.map(tab => (
        <li
          key={`tab-${tab.props.id}`}
          className={classnames(styles.tab, {
            [styles.isCurrent]: tab.props.id === current,
          })}
        >
          <Button
            href={getUrlForTab(tab.props.id)}
            className={styles.button}
            data-cosmetic
            data-no-scrolltop
            transparent
            full
          >
            {tab.props.label}
          </Button>
        </li>
      ))}
    </ul>
    {tabs.map(tab =>
      tab.props.id === current || viewedTabs[tab.props.id] ? (
        <div
          className={styles.tabContent}
          style={tab.props.id !== current ? hiddenStyle : null}
          key={`tabContent-${tab.props.id}`}
        >
          {tab.props.children}
        </div>
      ) : null,
    )}
  </React.Fragment>
);
const Accordion = ({ tabs, current, getUrlForTab, scrollIntoView }) => {
  const currentTabRef = useRef(null);
  useEffect(() => {
    if (currentTabRef.current) {
      const ref = currentTabRef.current;
      forceCheck();
      setTimeout(() => {
        if (ref === currentTabRef.current && scrollIntoView) {
          currentTabRef.current.scrollIntoView({
            behavior: 'auto',
            block: 'start',
            inline: 'nearest',
          });
        }
      }, 300);
    }
  }, [current]);
  return (
    <dl>
      {tabs.map(tab => {
        const tabId = tab.props.id;
        const isCurrent = tabId === current;
        return (
          <React.Fragment key={tabId}>
            <dt
              id={`accordion-${tabId}`}
              className={classnames(styles.accordionTitle, {
                [styles.isCurrent]: isCurrent,
              })}
              ref={isCurrent ? currentTabRef : null}
              role="heading"
              aria-level="2"
            >
              <Button
                href={isCurrent ? getUrlForTab() : getUrlForTab(tab.props.id)}
                className={styles.button}
                data-update
                data-cosmetic
                data-no-scrolltop
                transparent
                full
                aria-expanded={isCurrent}
                aria-controls={`accordion-${tabId}`}
              >
                {tabId == 'sustainability' ? 
                <Icon
                  icon={sustainability} 
                  className={classnames(styles.iconAfter, styles.iconSustainabilityHeader)}
                  /> 
                  : ''}
                {tab.props.label}
                <span className={styles.buttonIcon}>
                  <Icon
                    role="presentation"
                    icon={isCurrent ? minus : plus}
                    className={styles.iconAfter}
                  />
                </span>
              </Button>
            </dt>
            <dd
              className={classnames(
                styles.description,
                styles.accordionContent,
                {
                  [styles.accordionClosed]: !isCurrent,
                },
              )}
              aria-labelledby={`accordion-${tabId}`}
            >
              {tab.props.lazy
                ? isCurrent
                  ? tab.props.children
                  : null
                : tab.props.children}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
};

const ProductPresentationTabs = ({
  children,
  current,
  getUrlForTab,
  scrollIntoView,
  mode = ProductPresentationTabs.modes.tabs,
}) => {
  const tabs = React.Children.toArray(children);
  const [viewedTabs, setViewedTabs] = useState({});

  useEffect(() => {
    setViewedTabs(state => ({
      ...state,
      [current]: true,
    }));
  }, [current]);

  return mode === ProductPresentationTabs.modes.accordion ? (
    <Accordion
      tabs={tabs}
      current={current || null}
      getUrlForTab={getUrlForTab}
      scrollIntoView={scrollIntoView}
    />
  ) : (
    <Tabs
      tabs={tabs}
      current={current || tabs[0].props.id}
      viewedTabs={viewedTabs}
      getUrlForTab={getUrlForTab}
    />
  );
};

ProductPresentationTabs.modes = {
  accordion: 'accordion',
  tabs: 'tabs',
};
ProductPresentationTabs.propTypes = {
  current: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.oneOfType([Tab])),
  mode: PropTypes.oneOf(Object.keys(ProductPresentationTabs.modes)),
  getUrlForTab: PropTypes.func.isRequired,
  scrollIntoView: PropTypes.bool,
};
ProductPresentationTabs.defaultProps = {
  identifier: 'slug',
  mode: ProductPresentationTabs.modes.tabs,
  scrollIntoView: true,
};

ProductPresentationTabs.displayName = 'ProductPresentationTabs';
ProductPresentationTabs.propTypes = {};
ProductPresentationTabs.defaultProps = {};
ProductPresentationTabs.Tab = Tab;

export default ProductPresentationTabs;
